import type { QueryClient } from '@tanstack/react-query'

import {
  calendarDateSchema,
  directoryQuerySchema,
  opaquePublicIdSchema,
  playerQuerySchema,
  teamQuerySchema,
} from '@ballpeek/api/client'
import { z } from 'zod'

import {
  footballCoreBackTargetSchema,
  footballCoreLocale,
  isSafeFootballCoreBackTarget,
} from '~/lib/navigation/football-core'
import { parsePublicFootballRouteInput } from '~/lib/public-football-read'

import {
  directoryQueryOptions,
  playerProfileQueryOptions,
  teamProfileQueryOptions,
} from './query-options'

const backSchema = z.string().trim().min(1).max(4096).refine(
  value => isSafeFootballCoreBackTarget(value, 'en') || isSafeFootballCoreBackTarget(value, 'zh-CN'),
  'Expected a relative Football Core path',
)
function validateEntityBack(back: string | undefined, locale: string | undefined): void {
  if (back !== undefined) {
    parsePublicFootballRouteInput(
      footballCoreBackTargetSchema(footballCoreLocale(locale)),
      back,
    )
  }
}

export const directorySearchSchema = z.object({
  type: directoryQuerySchema.shape.type,
  q: z.union([z.literal(''), directoryQuerySchema.shape.q]).optional().default(''),
  page: directoryQuerySchema.shape.page,
}).strict()

export const teamProfileSearchSchema = z.object({
  playerPage: teamQuerySchema.shape.playerPage,
  back: backSchema.optional(),
}).strict()

export const playerProfileSearchSchema = z.object({
  appearancePage: playerQuerySchema.shape.appearancePage,
  at: calendarDateSchema.optional(),
  back: backSchema.optional(),
}).strict()

export const directoryRouteModule = {
  validateSearch: (search: unknown) => parsePublicFootballRouteInput(directorySearchSchema, search),
  loaderDeps: ({ search }: { search: z.infer<typeof directorySearchSchema> }) => search,
  loader: async ({ context, deps }: {
    context: { queryClient: QueryClient }
    deps: z.infer<typeof directorySearchSchema>
  }) => {
    if (deps.q.trim().length >= 2)
      await context.queryClient.ensureQueryData(directoryQueryOptions(deps))
  },
}

export const teamProfileRouteModule = {
  validateSearch: (search: unknown) => parsePublicFootballRouteInput(teamProfileSearchSchema, search),
  beforeLoad: ({ params, search }: {
    params: { teamId: string, locale?: string }
    search: z.infer<typeof teamProfileSearchSchema>
  }) => {
    parsePublicFootballRouteInput(opaquePublicIdSchema, params.teamId)
    validateEntityBack(search.back, params.locale)
  },
  loaderDeps: ({ search }: { search: z.infer<typeof teamProfileSearchSchema> }) => search,
  loader: async ({ context, params, deps }: {
    context: { queryClient: QueryClient }
    params: { teamId: string }
    deps: z.infer<typeof teamProfileSearchSchema>
  }) => {
    await context.queryClient.ensureQueryData(teamProfileQueryOptions(params.teamId, deps.playerPage))
  },
}

export const playerProfileRouteModule = {
  validateSearch: (search: unknown) => parsePublicFootballRouteInput(playerProfileSearchSchema, search),
  beforeLoad: ({ params, search }: {
    params: { playerId: string, locale?: string }
    search: z.infer<typeof playerProfileSearchSchema>
  }) => {
    parsePublicFootballRouteInput(opaquePublicIdSchema, params.playerId)
    validateEntityBack(search.back, params.locale)
  },
  loaderDeps: ({ search }: { search: z.infer<typeof playerProfileSearchSchema> }) => search,
  loader: async ({ context, params, deps }: {
    context: { queryClient: QueryClient }
    params: { playerId: string }
    deps: z.infer<typeof playerProfileSearchSchema>
  }) => {
    await context.queryClient.ensureQueryData(playerProfileQueryOptions(
      params.playerId,
      deps.appearancePage,
      deps.at,
    ))
  },
}
